import './PolicyStyles.css';
import { exportToWord } from '../utils/exportToWord';

const EnvironmentalPolicy = ({
  clientName,
  companyNumber,
  clientAddress,
  effectiveDate,
  environmentalOfficerContact,
}) => {
  const handleExportToWord = () => {
    const policyContent = document.getElementById('policy-content');
    exportToWord(policyContent.innerHTML, "Environmental_Policy_Extensive.docx");
  };

  return (
    <div className="policy-container" id="policy-content">
      <h3 className="policy-title">Environmental Policy</h3>

      <p className="policy-details"><strong>Company Name:</strong> {clientName || "[Insert Company Name]"}</p>
      <p className="policy-details"><strong>Company Number:</strong> {companyNumber || "[Insert Company Number]"}</p>
      <p className="policy-details"><strong>Company Address:</strong> {clientAddress || "[Insert Company Address]"}</p>
      <p className="policy-details"><strong>Policy Effective Date:</strong> {effectiveDate || "[Insert Effective Date]"}</p>

      <h4 className="section-title">1. Purpose</h4>
      <p className="section-content">
        {clientName || "The Company"} recognises the critical importance of environmental stewardship and is dedicated to leading by example in the transport industry. We commit to proactive measures that reduce our environmental footprint, conserve natural resources, and foster a culture of sustainability throughout our operations.
      </p>

      <h4 className="section-title">2. Scope</h4>
      <p className="section-content">
        This policy covers all activities across {clientName || "the Company"}, including fleet management, facilities, supply chain, procurement, and employee behaviour. It applies to all employees, contractors, suppliers, and stakeholders who have a role in our environmental performance.
      </p>

      <h4 className="section-title">3. Our Environmental Commitments</h4>
      <ul>
        <li><strong>Legal Compliance:</strong> Adhere strictly to all relevant environmental laws, regulations, and standards at local, national, and international levels.</li>
        <li><strong>Carbon Footprint Reduction:</strong> Continuously work to measure, monitor, and reduce greenhouse gas emissions across all operations.</li>
        <li><strong>Energy Efficiency:</strong> Implement energy-saving technologies and promote responsible energy consumption in all facilities and vehicles.</li>
        <li><strong>Waste Management:</strong> Minimise waste generation through reduction, reuse, and recycling initiatives, aiming for zero landfill waste.</li>
        <li><strong>Water Conservation:</strong> Monitor water use and implement practices to conserve this vital resource wherever possible.</li>
        <li><strong>Sustainable Procurement:</strong> Prioritise suppliers and products with strong environmental credentials.</li>
        <li><strong>Pollution Prevention:</strong> Prevent pollution of air, water, and soil by adopting best practices and proactive controls.</li>
        <li><strong>Environmental Awareness:</strong> Provide ongoing training and communication to engage employees in sustainability goals.</li>
        <li><strong>Innovation and Improvement:</strong> Foster a culture of innovation to identify new technologies and methods that advance environmental performance.</li>
      </ul>

      <h4 className="section-title">4. Fleet & Transport Practices</h4>
      <ul>
        <li>Regular maintenance of vehicles to optimise fuel efficiency and reduce emissions.</li>
        <li>Adoption of low-emission or alternative fuel vehicles where feasible.</li>
        <li>Route planning to minimise mileage and fuel consumption.</li>
        <li>Driver training focused on eco-friendly driving techniques such as smooth acceleration, appropriate speed, and reduced idling.</li>
        <li>Use of telematics and GPS monitoring to optimise performance and identify inefficiencies.</li>
      </ul>

      <h4 className="section-title">5. Facilities and Operations</h4>
      <ul>
        <li>Implement energy-efficient lighting, heating, and cooling systems.</li>
        <li>Promote recycling schemes for paper, plastics, metals, and electronic waste.</li>
        <li>Reduce single-use plastics and encourage reusable alternatives.</li>
        <li>Install water-saving devices and encourage mindful water use.</li>
        <li>Ensure responsible disposal of hazardous materials and waste.</li>
      </ul>

      <h4 className="section-title">6. Supply Chain and Procurement</h4>
      <p className="section-content">
        We actively seek to work with suppliers and contractors who share our environmental values and demonstrate sustainable practices.
      </p>
      <ul>
        <li>Incorporate environmental criteria in supplier selection and contract management.</li>
        <li>Encourage suppliers to reduce packaging and transport emissions.</li>
        <li>Collaborate with partners to promote sustainable innovation.</li>
      </ul>

      <h4 className="section-title">7. Monitoring, Reporting, and Review</h4>
      <p className="section-content">
        We commit to transparent reporting of environmental performance metrics including fuel consumption, emissions, waste management, and energy use. Environmental objectives and targets will be set, monitored, and reviewed annually to drive continuous improvement.
      </p>

      <h4 className="section-title">8. Employee Engagement and Training</h4>
      <p className="section-content">
        {clientName || "The Company"} fosters an environmental culture through:
      </p>
      <ul>
        <li>Regular training and awareness programs for all employees.</li>
        <li>Encouraging suggestions and initiatives from staff to enhance sustainability.</li>
        <li>Promoting green behaviours both at work and in the community.</li>
      </ul>

      <h4 className="section-title">9. Emergency Preparedness and Pollution Control</h4>
      <p className="section-content">
        Procedures are in place to prevent, respond to, and mitigate environmental emergencies such as spills, leaks, or other pollution events. These include training, containment strategies, and communication plans.
      </p>

      <h4 className="section-title">10. Governance and Accountability</h4>
      <p className="section-content">
        The Environmental Officer is responsible for overseeing the implementation of this policy and reporting progress to senior management and relevant stakeholders.
      </p>
      <p className="policy-details"><strong>Environmental Officer Contact:</strong> {environmentalOfficerContact || "[Insert Contact Number]"}</p>

      <h4 className="section-title">11. Policy Review</h4>
      <p className="section-content">
        This policy will be reviewed annually or when significant regulatory, operational, or environmental changes occur to ensure ongoing relevance and effectiveness.
      </p>

      <h4 className="section-title">Employee Acknowledgment and Signature</h4>
      <p>I acknowledge that I have read, understood, and commit to supporting the Environmental Policy and sustainability goals of {clientName || "the Company"}.</p>
      <p>Employee Name: _________________________</p>
      <p>Employee Signature: _________________________</p>
      <p>Date: _________________________</p>

      <h4 className="section-title">Management Approval and Signature</h4>
      <p>This policy has been reviewed and approved by senior management.</p>
      <p>Manager/Officer Name: _________________________</p>
      <p>Title: _________________________</p>
      <p>Signature: _________________________</p>
      <p>Date: _________________________</p>

      <div className="mt-4 text-center">
        <button
          onClick={handleExportToWord}
          className="bg-green-700 text-white px-6 py-2 rounded hover:bg-green-800 transition"
        >
          Export to Word
        </button>
      </div>
    </div>
  );
};

export default EnvironmentalPolicy;
